/**
 * The owner's answer to "is my business okay?".
 *
 * Written for somebody who has never seen a balance sheet and is reading on a
 * phone, probably standing up, probably at closing time. Three things follow
 * from that and shape everything below:
 *
 *  1. **The verdict comes first, in words.** Not a table of figures to be
 *     interpreted — a sentence that says whether money is missing, and how
 *     much. Tables are underneath for the owner who wants to check.
 *  2. **Nothing is computed here.** Every number is the API's. A total added
 *     up in the browser is a second implementation of the accounting, and the
 *     two will disagree eventually — always at the worst moment.
 *  3. **Currency and timezone come from the business.** No symbol and no
 *     calendar is assumed; a Lagos shop and a New York one each see their own.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CheckCircle2,
  ChevronDown,
  Package,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { ApiError, api } from '../../lib/api';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Spinner } from '../../components/ui';
import {
  PRESET_LABELS,
  money,
  moment,
  percent,
  quantity,
  resolvePreset,
  varianceTone,
  type DateRange,
  type PresetKey,
  type Tone,
} from './format';
import { StockTable } from './StockTable';
import { StaffTable } from './StaffTable';
import { FlagList } from './FlagList';
import { CashPanel } from './CashPanel';
import type { AccountabilityReport } from './types';

const TONE_CLASSES: Record<Tone, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  bad: 'border-rose-200 bg-rose-50 text-rose-900',
  neutral: 'border-slate-200 bg-white text-slate-900',
};

function Metric({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: string;
  tone?: Tone;
  hint?: string;
}): ReactNode {
  return (
    <div className={`rounded-xl border p-3 ${TONE_CLASSES[tone]}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums sm:text-xl">{value}</p>
      {hint && <p className="mt-0.5 text-xs opacity-70">{hint}</p>}
    </div>
  );
}

export function AccountabilityPage(): ReactNode {
  const { business, can } = useAuth();
  const currency = business?.currencyCode ?? 'USD';
  // The business's calendar, not the browser's. See format.ts.
  const timezone = business?.timezone ?? 'UTC';

  const [preset, setPreset] = useState<PresetKey>('today');
  const [custom, setCustom] = useState<DateRange>(() => resolvePreset('today', timezone));
  const [openSection, setOpenSection] = useState<'stock' | 'cash' | 'staff' | 'flags'>('flags');

  const range = useMemo(
    () => (preset === 'custom' ? custom : resolvePreset(preset, timezone)),
    [preset, custom, timezone],
  );

  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const report = useQuery({
    queryKey: ['accountability', business?.businessId, range.from, range.to],
    queryFn: () =>
      api.get<AccountabilityReport>(
        path(`/accountability?from=${range.from}&to=${range.to}`),
      ),
    enabled: Boolean(business),
  });

  if (!can('reports:read')) {
    return (
      <EmptyState
        icon={<ShieldAlert className="h-6 w-6" />}
        title="Not available to your role"
        description="This screen shows who removed stock and whose drawer was short. Ask the owner if you need access."
      />
    );
  }

  const data = report.data;
  const shortage = data ? Number(data.cash.variance ?? 0) : 0;
  const hasCount = Boolean(data?.cash.counted);
  const urgent = data?.flags.filter((f) => f.severity === 'investigate').length ?? 0;

  return (
    <div className="space-y-4 pb-10">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {data && data.stock === null ? 'Finance' : 'Accountability'}
        </h1>
        <p className="text-sm text-slate-500">
          What came in, what was sold, and whether the money adds up.
        </p>
      </header>

      {/* Presets scroll horizontally on a phone rather than wrapping into a
          block that pushes the numbers below the fold. */}
      <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
        {(Object.keys(PRESET_LABELS) as PresetKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setPreset(key)}
            aria-pressed={preset === key}
            className={`shrink-0 rounded-lg px-3 py-1.5 text-sm ${
              preset === key
                ? 'bg-indigo-700 text-white'
                : 'border border-slate-200 bg-white text-slate-600'
            }`}
          >
            {PRESET_LABELS[key]}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-500">
            From
            <input
              type="date"
              value={custom.from}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-500">
            To
            <input
              type="date"
              value={custom.to}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-1 text-sm"
            />
          </label>
        </div>
      )}

      {report.isLoading && (
        <div className="flex justify-center p-12">
          <Spinner label="Working out the figures" />
        </div>
      )}

      {report.isError && (
        <ErrorState
          message={
            report.error instanceof ApiError
              ? report.error.message
              : 'Could not load the figures. Try again in a moment.'
          }
          onRetry={() => void report.refetch()}
        />
      )}

      {data && (
        <>
          {/*
            The verdict, in one sentence, before any table.
            An owner who reads only this should still know whether to worry.
          */}
          <section
            className={`rounded-2xl border p-4 ${
              !hasCount
                ? TONE_CLASSES.neutral
                : shortage < 0
                  ? TONE_CLASSES.bad
                  : shortage > 0
                    ? TONE_CLASSES.warn
                    : TONE_CLASSES.good
            }`}
          >
            <div className="flex items-start gap-3">
              {hasCount && shortage < 0 ? (
                <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0" aria-hidden />
              ) : hasCount && shortage === 0 ? (
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0" aria-hidden />
              ) : (
                <Banknote className="mt-0.5 h-6 w-6 shrink-0" aria-hidden />
              )}

              <div className="min-w-0">
                <p className="text-lg font-semibold sm:text-xl">
                  {!hasCount
                    ? 'Nobody has counted the cash yet'
                    : shortage < 0
                      ? `Money is missing: ${money(Math.abs(shortage), currency)}`
                      : shortage > 0
                        ? `There is more cash than expected: ${money(shortage, currency)}`
                        : 'The cash matches exactly'}
                </p>

                <p className="mt-1 text-sm opacity-80">
                  Expected {money(data.cash.expected, currency)}
                  {hasCount && <> · Counted {money(data.cash.counted, currency)}</>}
                </p>
                {data.cash.loans && (Number(data.cash.loans.cashIn) !== 0 || Number(data.cash.loans.cashOut) !== 0) && (
                  <p className="mt-1 text-xs opacity-80">
                    Includes cash loans: {money(data.cash.loans.cashOut, currency)} paid out,{' '}
                    {money(data.cash.loans.cashIn, currency)} repaid. Loans move cash, never profit.
                  </p>
                )}

                {/*
                  An overage is not good news. It usually means a sale went
                  unrecorded, which is the same failure wearing a friendlier face.
                */}
                {hasCount && shortage > 0 && (
                  <p className="mt-1 text-sm opacity-80">
                    Extra cash usually means a sale was not recorded.
                  </p>
                )}

                {urgent > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpenSection('flags')}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2"
                  >
                    {urgent} {urgent === 1 ? 'issue needs' : 'issues need'} your attention
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Two columns on a phone, four on a laptop. */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
            <Metric label="Sales" value={money(data.revenue.gross, currency)} />
            {/*
              Gross profit and margin are sales less the cost of stock sold. A
              business without Inventory has no stock cost, so they would only
              repeat the sales figure — left out rather than shown as 100%.
            */}
            {data.stock && (
              <>
                <Metric label="Gross profit" value={money(data.revenue.grossProfit, currency)} />
                <Metric label="Margin" value={percent(data.revenue.margin)} />
                <Metric label="Stock value" value={money(data.stock.onHandValue, currency)} />
              </>
            )}
            <Metric label="Expected cash" value={money(data.cash.expected, currency)} />
            <Metric
              label="Counted cash"
              value={hasCount ? money(data.cash.counted, currency) : 'Not counted'}
              tone={hasCount ? 'neutral' : 'warn'}
            />
            <Metric
              label="Cash variance"
              value={hasCount ? money(data.cash.variance, currency) : '—'}
              tone={varianceTone(hasCount ? data.cash.variance : null)}
            />
            <Metric
              label="Needs attention"
              value={String(urgent)}
              tone={urgent > 0 ? 'bad' : 'good'}
              hint={data.flags.length > urgent ? `${data.flags.length - urgent} to watch` : undefined}
            />
          </div>

          {/*
            Sections are collapsible and one is open at a time. On a phone,
            four expanded tables is a scroll nobody finishes.
          */}
          <Section
            id="flags"
            title="Needs attention"
            icon={<ShieldAlert className="h-4 w-4" aria-hidden />}
            count={data.flags.length}
            open={openSection === 'flags'}
            onToggle={setOpenSection}
          >
            <FlagList flags={data.flags} currency={currency} timezone={timezone} />
          </Section>

          <Section
            id="cash"
            title="Cash"
            icon={<Banknote className="h-4 w-4" aria-hidden />}
            open={openSection === 'cash'}
            onToggle={setOpenSection}
          >
            <CashPanel
              cash={data.cash}
              currency={currency}
              timezone={timezone}
              businessId={business?.businessId ?? ''}
            />
          </Section>

          {data.stock && (
            <Section
              id="stock"
              title="Stock"
              icon={<Package className="h-4 w-4" aria-hidden />}
              count={data.stock.lines.length}
              open={openSection === 'stock'}
              onToggle={setOpenSection}
            >
              <StockTable
                lines={data.stock.lines}
                currency={currency}
                timezone={timezone}
                businessId={business?.businessId ?? ''}
              />
            </Section>
          )}

          <Section
            id="staff"
            title="Staff"
            icon={<Users className="h-4 w-4" aria-hidden />}
            count={data.staff.length}
            open={openSection === 'staff'}
            onToggle={setOpenSection}
          >
            <StaffTable staff={data.staff} currency={currency} />
          </Section>
        </>
      )}
    </div>
  );
}

function Section({
  id,
  title,
  icon,
  count,
  open,
  onToggle,
  children,
}: {
  id: 'stock' | 'cash' | 'staff' | 'flags';
  title: string;
  icon: ReactNode;
  count?: number;
  open: boolean;
  onToggle: (id: 'stock' | 'cash' | 'staff' | 'flags') => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Card>
      <button
        type="button"
        onClick={() => onToggle(open ? ('flags' as const) : id)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 p-4 text-left"
      >
        <span className="flex items-center gap-2 font-semibold text-slate-900">
          {icon}
          {title}
          {count !== undefined && (
            <Badge tone="neutral">{count}</Badge>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && <div className="border-t border-slate-100 p-4">{children}</div>}
    </Card>
  );
}
